import * as React from 'react';
import { BrowserEventName, PseudoEventName, TableauMouseButton } from '../../gen-src/preslayer/Enums';
import { BrowserSupport } from '@tableau/browser-support';
import { EventInputType } from './EventInputType';
import { EventRecognizer } from '../EventRecognizer';
import { EventSource } from './EventSource';
import { GestureEventInfo } from '../gesture/Gesture';
import { MiscUtil } from '../utility/MiscUtil';
import { MouseEventUtil } from '../utility/MouseEventUtil';
import { NormalizedEventBuilder } from './NormalizedEventBuilder';
import { SelectAction } from './SelectAction';
import { TypeUtil } from '../utility/TypeUtil';

// ------------------------------------
// Helper functions

class TouchListImpl extends Array {
  [key: number]: Touch | null;

  public item(index: number): Touch | null {
    return this[index];
  }
}

/**
* Wrapper class around touch and mouse events to normalize them for our event handling system. Generally,
* the NormalizedEvent object looks more like a TouchEvent object than a MouseEvent object. See the comments
* on the fields for the API.
*/
class NormalizedEvent {
  // ===========================================================================================================
  // Member Variables
  // ===========================================================================================================

  // the original event object. If you find yourself using this, it's probably a flaw in the design
  // of this NormalizedEvent class, and this class should be changed
  public originalEvent: TouchEvent;

  // Only set if this originated from a react synthetic event
  public reactEvent: React.SyntheticEvent<HTMLDivElement>;

  public inputType: EventInputType;

  public source: EventSource;

  public type: BrowserEventName;        // same as mouse event
  public target: HTMLElement;           // same as mouse event
  public currentTarget: HTMLElement;    // same as mouse event
  public timeStamp: number;             // same as mouse event
  public keyCode: number;               // same as mouse event

  /**
  * This is probably not the field you're looking for.  Have a look at <see cref="IsCtrlKey"/> instead.
  */
  public ctrlKey: boolean;              // same as mouse event
  public altKey: boolean;               // same as mouse event

  /**
  * This is probably not the field you're looking for.  Have a look at <see cref="IsCtrlKey"/> instead.
  */
  public metaKey: boolean;              // same as mouse event
  public shiftKey: boolean;             // same as mouse event

  public clientX: number;               // same as jQueryEvent
  public clientY: number;               // same as jQueryEvent

  public pageX: number;
  public pageY: number;

  // WARNING: touches, targetTouches, and changedTouches disguise mouse events simply by wrapping the originalEvent
  // in an array: [e]. Thus, the "Touch Objects" inside of the arrays are not normalized. A potential bug here is
  // when referring to the target of a touch object: touches[0].target is different for a touch object and a mouse
  // event object. However, if you find yourself referring to the target of Touch Objects (as opposed to the Target
  // of the Event object), either you're specifically in a Touch context and have multiple touches happening at once
  // that might be on different targets, you're doing something you shouldn't be doing, or you're in a use case I did
  // not foresee when writing this comment. If the last option is true, you're on your own.
  public touches: TouchList;          // TouchObject array with iOS behavior (empty onmouseup or ontouchend)

  public targetTouches: TouchList;    // TouchObject array with iOS behavior (empty onmouseup or ontouchend)
  public changedTouches: TouchList;   // TouchObject array with iOS behavior

  // Allows lookup of touch by its unique identifier. With the other touch arrays (touches,
  // targetTouches, changedTouches), there's no guarantee that a touch at touches[1] in a
  // touchstart event will be the same touch at touches[1] in a touchmove event. Thus, use
  // the identifier.
  public touchesById: Touch[];

  public gestureName: string;

  public callbackName: PseudoEventName;

  private static MouseIdentifier: number = 0; // touches have unique identifier codes. Use this for a mouse event disguised as a touch

  // ===========================================================================================================
  // Constructors
  // ===========================================================================================================

  /**
  * Initializes a new instance of the NormalizedEvent class
  *
  * @param {TouchEvent} evt - TouchEvent object from Browser
  * @param {HTMLElement} listeningNode - Element object of the relevant node
  * @param {React.SyntheticEvent} reactEvent - Only necessary if coming from the React world
  */
  public constructor(
    evt: TouchEvent,
    listeningNode: HTMLElement,
    reactEvent: React.SyntheticEvent<HTMLDivElement> = null) {
    this.originalEvent = evt;
    this.reactEvent = reactEvent;
    this.type = evt.type as BrowserEventName;
    this.timeStamp = evt.timeStamp;
    this.keyCode = TypeUtil.GetField<number>(evt, 'keyCode');
    this.ctrlKey = evt.ctrlKey;
    this.altKey = evt.altKey;
    this.metaKey = evt.metaKey;
    this.shiftKey = evt.shiftKey;
    this.clientX = TypeUtil.GetField<number>(evt, 'clientX');
    this.clientY = TypeUtil.GetField<number>(evt, 'clientY');

    // Both iOS and Android browsers make the target of an event the textNode, not the Element. W3 decided that
    // this is dumb (on 3/25/2011), and we think it is too. Targets should be elements, not text nodes
    // $NOTE-kjacyna-2020-02-25: This problem also occurs in Firefox - updating this check to apply to
    // mouse events as well // TFSID: 1073360 [Firefox] Slow metadata search throws TypeError: 'matches' called
    // on an object that does not implement interface Element.
    let target: HTMLElement = evt.target as HTMLElement;
    // tslint:disable-next-line:strict-boolean-expressions
    this.target = target && target.nodeType === 3 /*XmlNodeType.Text*/ ? target.parentNode as HTMLElement : target;

    if (!MiscUtil.IsNullOrUndefined(evt.touches)) {
      // we're in a touch environment

      // $NOTE-myork-2011-04-25: Hello person from the future. This doesn't normalize for Android vs iOS differences.
      // The TouchObjects in the touches, targetTouches arrays are different ontouchend. I don't know the exact details
      // - those are for whoever has to implement Android support.
      this.touches = evt.touches;
      this.targetTouches = evt.targetTouches;
      this.changedTouches = evt.changedTouches;

      // touch objects don't provide the currentTarget that the event listener is attached to
      this.currentTarget = listeningNode;
    } else {
      // we're in a mouse environment

      // touches and targetTouches should be empty onmouseup
      // tslint:disable:whitespace
      let tmpArray = new TouchListImpl();
      tmpArray[0] = evt as object as Touch; // wow this is still shady
      this.targetTouches = (evt.type === BrowserEventName.MouseUp) ? new TouchListImpl() : tmpArray;
      this.touches = this.targetTouches;
      this.changedTouches = new TouchListImpl();
      this.changedTouches[0] = evt as object as Touch;
      this.currentTarget = evt.currentTarget as HTMLElement;
      // tslint:enable:whitespace
    }
    // build associative array so we can grab touches by their unique identifier.
    let i: number = this.touches.length;
    this.touchesById = new Array(i);
    while (i > 0) {
      i -= 1;
      // tslint:disable-next-line:strict-boolean-expressions
      let index: number = this.touches[i].identifier || NormalizedEvent.MouseIdentifier;
      this.touchesById[index] = this.touches[i];
    }
    i = this.changedTouches.length;
    while (i > 0) {
      i -= 1;
      // tslint:disable-next-line:strict-boolean-expressions
      let index: number = this.changedTouches[i].identifier || NormalizedEvent.MouseIdentifier;
      this.touchesById[index] = this.changedTouches[i];
    }
  }

  // ===========================================================================================================
  // Properties
  // ===========================================================================================================

  public get IsMouseEvent(): boolean {
    return this.source === EventSource.Mouse;
  }

  public get IsTouchEvent(): boolean {
    return this.source === EventSource.Touch;
  }

  public get IsStartEvent(): boolean {
    return this.inputType === EventInputType.Start;
  }

  public get IsMoveEvent(): boolean {
    return this.inputType === EventInputType.Move;
  }

  public get IsOutEvent(): boolean {
    return this.inputType === EventInputType.Out;
  }

  public get IsEndEvent(): boolean {
    return this.inputType === EventInputType.End;
  }

  public get IsScrollEvent(): boolean {
    return this.inputType === EventInputType.Scroll;
  }

  public get IsRightClickEvent(): boolean {
    return (this.type === BrowserEventName.ContextMenu && this.source === EventSource.Mouse);
  }

  public get IsLeftMouseEvent(): boolean {
    if (this.IsMouseEvent) {
      let mouseBtn: TableauMouseButton = this.RetrieveMouseButton();
      if (mouseBtn === TableauMouseButton.Left) {
        // on mac, left button + ctrl key = contextmenu event so should actually be treated as a right-click
        if (BrowserSupport.IsMac() && this.ctrlKey) {
          return false;
        }
        return true;
      }
    }
    return false;
  }

  public get IsRightMouseEvent(): boolean {
    if (this.IsMouseEvent) {
      let mouseBtn: TableauMouseButton = this.RetrieveMouseButton();
      if (mouseBtn === TableauMouseButton.Right) {
        return true;
      } else {
        if (mouseBtn === TableauMouseButton.Left && BrowserSupport.IsMac() && this.altKey) {
          // on mac, left button + option key = should be treated as a right-drag
          return true;
        }
      }
    }
    return false;
  }

  /**
  * @returns {boolean} true if the Ctrl key is pressed on Windows, or the meta(command) key is pressed on Mac.
  */
  public get IsCtrlKey(): boolean {
    return BrowserSupport.IsMac() ? this.metaKey : this.ctrlKey;
  }

  /**
  * Gets the gesture event info for this event.
  */
  public get GestureInfo(): GestureEventInfo {
    // Gesture event info gets "mixed in" using jQuery.Extend.
    return this as object as GestureEventInfo;
  }

  /**
  * Returns whether or not all touches in the event object share a currentTarget.
  * Only pays attention to the first two touches in the touches array.
  * Preconditions:
  *   - more than 1 touch in e.touches in array
  *   - one touch  must be a child of the target (implicitly true
  *     by being inside of the EventHandler - one of the touches must be a child of
  *     the currentTarget, otherwise we would never have received a touch event object).
  *
  * We need to let Safari do its default viewport zoom for a pinch where the targets
  * are different. Unfortunately, we have no way to get the currentTarget of touch objects.
  * It's just not provided. So we do the dirty work here.
  */
  public AllTouchesShareCurrentTarget(): boolean {
    let node1: EventTarget = this.touches[0].target;  // a Touch Object's target behaves like currentTarget for a mouse event
    let node2: EventTarget = this.touches[1].target;

    // Touch Target is of type EventTarget and it could be either Element(XmlElement in Salterelle), Document or Window.
    // If any one of the touch target is
    // not of the type Element, then we can safely determine that touches don't share target.
    if (!(node1 instanceof Element) || !(node2 instanceof Element)) {
      return false;
    }
    let body: Element = document.body;
    let currentTarget: Element = this.currentTarget;
    let done: boolean = false;

    // walk up the tree until each element reaches the target or the body.
    while (!done && !MiscUtil.IsNullOrUndefined(node1) && !MiscUtil.IsNullOrUndefined(node2)) {
      done = true;
      if (node1 !== currentTarget && node1 !== body && node1 instanceof Element) {
        node1 = (node1 as Element).parentNode;
        done = false;
      }
      if (node2 !== currentTarget && node2 !== body && node2 instanceof Element) {
        node2 = (node2 as Element).parentNode;
        done = false;
      }

      // If both nodes converge while traversing, then they must share the same currentTarget,
      // so break now for efficiency's sake (this loop would still work without this check)
      if (node1 === node2) {
        done = true;
      }
    }
    return node1 === node2;
  }

  public static get AllHandledEvents(): Array<BrowserEventName> {
    return Object.keys(NormalizedEventBuilder.HandledEvents) as BrowserEventName[];
  }

  // ===========================================================================================================
  // Methods
  // ===========================================================================================================
  public static Create(
    evt: TouchEvent, listeningNode: HTMLElement, reactEvent: React.SyntheticEvent<HTMLDivElement> = null): NormalizedEvent {
    return NormalizedEventBuilder.CreateNormalizedEvent(evt, listeningNode, reactEvent);
  }

  private RetrieveMouseButton(): TableauMouseButton {
    // For mouse move events, we can't check the event for the move event
    // so we will rely on the last button clicked.
    //
    // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
    return this.IsMoveEvent ? EventRecognizer.CurrentGlobalMouseButton : MouseEventUtil.GetMouseButtonEnum(this.originalEvent);
  }

  /**
  * Determines what the selection mode is for a text area or a text box
  * based on the state of the keyboard keys.
  */
  public GetSelectAction(): SelectAction {
    let action: SelectAction = SelectAction.Simple;
    let toggle: boolean = this.IsCtrlKey;
    if (this.shiftKey) {
      action = SelectAction.Range;
    }
    if (toggle) {
      action = SelectAction.Toggle;
    }
    return action;
  }

  public preventDefault(): void {
    if (TypeUtil.HasMethod(this.originalEvent, 'preventDefault')) {
      this.originalEvent.preventDefault();
    }
  }

  public stopPropagation(): void {
    if (TypeUtil.HasMethod(this.originalEvent, 'stopPropagation')) {
      this.originalEvent.stopPropagation();
    }
    if (!MiscUtil.IsNullOrUndefined(this.reactEvent)) {
      this.reactEvent.stopPropagation();
    }
  }

  // redirection for saltarelle generated javascript getters
  public get_IsRightMouseEvent(): boolean { return this.IsRightMouseEvent; }
  public get_GestureInfo(): GestureEventInfo { return this.GestureInfo; }
  public get_IsMouseEvent(): boolean { return this.IsMouseEvent; }
  public get_IsTouchEvent(): boolean { return this.IsTouchEvent; }
  public get_IsStartEvent(): boolean { return this.IsStartEvent; }
  public get_IsMoveEvent(): boolean { return this.IsMoveEvent; }
  public get_IsOutEvent(): boolean { return this.IsOutEvent; }
  public get_IsEndEvent(): boolean { return this.IsEndEvent; }
  public get_IsScrollEvent(): boolean { return this.IsScrollEvent; }
  public get_IsRightClickEvent(): boolean { return this.IsRightClickEvent; }
  public get_IsLeftMouseEvent(): boolean { return this.IsLeftMouseEvent; }
  public get_IsCtrlKey(): boolean { return this.IsCtrlKey; }
}

export { NormalizedEvent };
